import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import type { SellerSummary } from '@seller-portal/types';
import { chipsToParam } from '@seller-portal/types';
import { fetchListings } from '@/features/catalog/api';
import { listingPath } from '@/features/catalog/ListingsTable';
import { useCampaigns } from '@/features/campaigns/api';
import { useSets } from '@/features/sets/api';
import { useDebounced } from '@/lib/useDebounced';

interface Hit {
  key: string;
  group: string;
  label: string;
  sub?: string;
  to: string;
}

/**
 * The top bar's search (prototype `GlobalSearch`): the seller's variants by name or item code
 * (catalog), product sets and campaigns by name. Enter opens Products with the text as a filter.
 */
export function GlobalSearch({ seller }: { seller: SellerSummary }) {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const q = useDebounced(text.trim());
  const ql = q.toLowerCase();
  const on = ql.length >= 2;
  const listings = useQuery({
    enabled: on,
    queryKey: ['seller', seller.id, 'search', q],
    queryFn: () => fetchListings(seller.id, { filter: { text: q }, pageSize: 6 }),
  });
  const sets = useSets(seller.id);
  const campaigns = useCampaigns(seller.id, seller.isDemo);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const hits: Hit[] = on
    ? [
        ...(listings.data?.listings ?? []).map((l) => ({
          key: `l:${l.id}`,
          group: 'Products',
          label: l.title || l.itemCode,
          sub: l.itemCode,
          to: listingPath(seller.id, l.id),
        })),
        ...(sets.data ?? [])
          .filter((s) => s.name.toLowerCase().includes(ql))
          .slice(0, 4)
          .map((s) => ({
            key: `s:${s.id}`,
            group: 'Product sets',
            label: s.name,
            to: `/${seller.id}/sets/${s.id}`,
          })),
        ...(campaigns.data?.campaigns ?? [])
          .filter((c) => c.name.toLowerCase().includes(ql))
          .slice(0, 4)
          .map((c) => ({
            key: `c:${c.id}`,
            group: 'Campaigns',
            label: c.name,
            sub: c.status,
            to: `/${seller.id}/campaigns/${c.id}`,
          })),
      ]
    : [];
  const allProducts = `/${seller.id}/products?f=${encodeURIComponent(chipsToParam([{ kind: 'text', path: '', values: [q] }]) ?? '')}`;

  const go = (to: string) => {
    setOpen(false);
    setText('');
    navigate(to);
  };

  return (
    <div ref={box} className="relative hidden max-w-[520px] flex-1 md:block">
      <label className="border-line2 bg-bg text-faint focus-within:border-cyan flex items-center gap-2 rounded-lg border px-2.5 py-[7px]">
        <Search size={15} strokeWidth={1.8} aria-hidden="true" />
        <input
          type="search"
          role="combobox"
          aria-expanded={open && on}
          aria-controls="global-search-results"
          aria-label="Search products, item codes, product sets and campaigns"
          placeholder="Search products, item codes, sets, campaigns"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
            setActive(0);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
            else if (e.key === 'ArrowDown') setActive((a) => Math.min(a + 1, hits.length));
            else if (e.key === 'ArrowUp') setActive((a) => Math.max(a - 1, 0));
            else if (e.key === 'Enter' && text.trim().length >= 2) {
              e.preventDefault();
              go(active < hits.length && hits[active] ? hits[active].to : allProducts);
            }
          }}
          className="text-ink w-full bg-transparent text-[13px] outline-none"
        />
      </label>
      {open && on ? (
        <div
          id="global-search-results"
          role="listbox"
          className="border-line bg-panel absolute left-0 right-0 top-full z-30 mt-1 max-h-[60vh] overflow-y-auto rounded-[10px] border py-1 shadow-lg"
        >
          {listings.isFetching && hits.length === 0 ? (
            <p className="text-muted px-3 py-2 text-xs">Searching…</p>
          ) : null}
          {hits.map((h, i) => (
            <div key={h.key}>
              {i === 0 || hits[i - 1]!.group !== h.group ? (
                <p className="text-faint px-3 pb-0.5 pt-2 font-mono text-[10px] uppercase tracking-[.07em]">
                  {h.group}
                </p>
              ) : null}
              <button
                type="button"
                role="option"
                aria-selected={i === active}
                onMouseEnter={() => setActive(i)}
                onClick={() => go(h.to)}
                className={`flex w-full items-baseline gap-2 px-3 py-1.5 text-left text-[12.5px] ${i === active ? 'bg-hover' : ''}`}
              >
                <span className="truncate font-medium">{h.label}</span>
                {h.sub ? (
                  <span className="text-faint shrink-0 font-mono text-[11px]">{h.sub}</span>
                ) : null}
              </button>
            </div>
          ))}
          <button
            type="button"
            role="option"
            aria-selected={active === hits.length}
            onMouseEnter={() => setActive(hits.length)}
            onClick={() => go(allProducts)}
            className={`text-blue border-line mt-1 w-full border-t px-3 py-2 text-left text-xs font-medium ${active === hits.length ? 'bg-hover' : ''}`}
          >
            All products matching &ldquo;{q}&rdquo;
          </button>
        </div>
      ) : null}
    </div>
  );
}
